import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from '../../Layout'
import useAuthStore from "../../store/authStore";
import useTableColumnSettings from "../../hooks/useTableColumnSettings";
import { ColumnSettingButton } from "../../components/TableColumnSetting";
import {
    Input,
    Table,
    Tag,
    Button,
    Popconfirm,
    message,
    Modal,
    Form,
    InputNumber,
    Select,
    Switch,
    Spin,
    Tabs,
} from "antd";
import {
    SearchOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
} from "@ant-design/icons";
import {
    getServiceCategories,
    createServiceCategory,
    updateServiceCategory,
    deleteServiceCategory,
    getTreatmentSlips,
    type ServiceCategoryItem,
    type TreatmentSlipItem,
} from "../../api/Service";

/* ================= FORMAT ================= */
const formatPrice = (num: number) =>
    num.toLocaleString("vi-VN") + " đ";

const normalizeVietnamese = (value?: string) =>
    (value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/đ/g, "d")
        .replace(/Đ/g, "D")
        .toLowerCase();

/* ================= COMPONENT ================= */
export default function ServiceTable() {
    const { t } = useTranslation();
    // Phân quyền thao tác: chỉ có quyền xem thì không hiện nút thêm/sửa/xóa
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canCreate = isSuperAdmin || hasPermission("service_categories.create");
    const canEdit = isSuperAdmin || hasPermission("service_categories.edit");
    const canDelete = isSuperAdmin || hasPermission("service_categories.delete");
    const STATUS_MAP: Record<number, { label: string; color: string }> = {
        0: { label: t('services.status_active'), color: "green" },
        1: { label: t('services.status_locked'), color: "red" },
    };

    /* ===== STATE ===== */
    const [data, setData] = useState<ServiceCategoryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [keyword, setKeyword] = useState("");
    const [tab, setTab] = useState("all");
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });

    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState<ServiceCategoryItem | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();
    const [sortField, setSortField] = useState<string | undefined>();
    const [sortOrder, setSortOrder] = useState<string | undefined>();
    const [treatmentSlipOptions, setTreatmentSlipOptions] = useState<TreatmentSlipItem[]>([]);
    const [loadingTreatmentSlips, setLoadingTreatmentSlips] = useState(false);

    /* ===== FETCH DATA ===== */
    const fetchData = useCallback(async (page = 1, search = keyword, statusFilter?: number, sort?: string) => {
        setLoading(true);
        try {
            const res = await getServiceCategories({
                page,
                take: pagination.pageSize,
                search: search || undefined,
                status: statusFilter,
                sort: sort || undefined,
            });
            if (res.status) {
                setData(res.data.items);
                setPagination(prev => ({
                    ...prev,
                    current: res.data.current_page,
                    total: res.data.total,
                }));
            }
        } finally {
            setLoading(false);
        }
    }, [keyword, pagination.pageSize]);

    useEffect(() => {
        const statusFilter = tab === 'active' ? 0 : tab === 'locked' ? 1 : undefined;
        const sort = sortField && sortOrder ? `${sortField},${sortOrder}` : undefined;
        fetchData(1, keyword, statusFilter, sort);
    }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchTreatmentSlipOptions = useCallback(async () => {
        setLoadingTreatmentSlips(true);
        try {
            const res = await getTreatmentSlips({ is_active: 1, page: 1, take: 100 });
            if (res.status) {
                setTreatmentSlipOptions(res.data.items || []);
            }
        } finally {
            setLoadingTreatmentSlips(false);
        }
    }, []);

    useEffect(() => {
        fetchTreatmentSlipOptions();
    }, [fetchTreatmentSlipOptions]);

    /* ===== SEARCH ===== */
    const handleSearch = (value: string) => {
        setKeyword(value);
        const statusFilter = tab === 'active' ? 0 : tab === 'locked' ? 1 : undefined;
        const sort = sortField && sortOrder ? `${sortField},${sortOrder}` : undefined;
        fetchData(1, value, statusFilter, sort);
    };

    /* ===== COUNT ===== */
    const countActive = data.filter((i) => i.status === 0).length;
    const countLocked = data.filter((i) => i.status === 1).length;

    /* ===== ACTION ===== */
    const openAdd = () => {
        setEditing(null);
        form.resetFields();
        form.setFieldsValue({ status: true, treatment_slip_id: undefined });
        setOpen(true);
    };

    const openEdit = (record: ServiceCategoryItem) => {
        setEditing(record);
        form.setFieldsValue({
            name: record.name,
            description: record.description || '',
            image: record.image || '',
            treatment_slip_id: record.treatment_slip_id || record.treatment_slip?.id || undefined,
            status: record.status === 0,
        });
        setOpen(true);
    };

    const handleDelete = async (id: number) => {
        const res = await deleteServiceCategory(id);
        if (res.status) {
            message.success(res.msg || t('services.deleted'));
            const statusFilter = tab === 'active' ? 0 : tab === 'locked' ? 1 : undefined;
            fetchData(pagination.current, keyword, statusFilter);
        } else {
            message.error(res.msg || t('services.delete_failed'));
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                name: values.name,
                description: values.description || undefined,
                image: values.image || undefined,
                treatment_slip_id: values.treatment_slip_id || undefined,
                status: values.status ? 0 : 1,
            };

            setSubmitting(true);
            if (editing) {
                const res = await updateServiceCategory(editing.id, payload);
                if (res.status) {
                    message.success(res.msg || t('services.updated'));
                } else {
                    message.error(res.msg || t('services.update_failed'));
                    return;
                }
            } else {
                const res = await createServiceCategory(payload);
                if (res.status) {
                    message.success(res.msg || t('services.added'));
                } else {
                    message.error(res.msg || t('services.add_failed'));
                    return;
                }
            }

            setOpen(false);
            const statusFilter = tab === 'active' ? 0 : tab === 'locked' ? 1 : undefined;
            fetchData(pagination.current, keyword, statusFilter);
        } catch {
            // validation error
        } finally {
            setSubmitting(false);
        }
    };

    /* ===== TABLE CHANGE ===== */
    const handleTableChange = (pag: any, _filters: any, sorter: any) => {
        const statusFilter = tab === 'active' ? 0 : tab === 'locked' ? 1 : undefined;
        const field = sorter.field;
        const order = sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : undefined;
        setSortField(order ? field : undefined);
        setSortOrder(order);
        const sort = field && order ? `${field},${order}` : undefined;
        fetchData(pag.current, keyword, statusFilter, sort);
    };

    /* ===== COLUMNS ===== */
    const columns = [
        {
            key: "stt",
            title: t('services.col_stt'),
            width: 60,
            render: (_: any, __: any, index: number) => (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: t('services.col_name'),
            dataIndex: "name",
            sorter: true,
            render: (val: string) => (
                <div className="font-medium">{val}</div>
            ),
        },
        {
            title: t('services.col_description'),
            dataIndex: "description",
            render: (val: string | null) => val || '-',
        },
        {
            title: "Phiếu chuyên môn",
            dataIndex: "treatment_slip",
            render: (val: ServiceCategoryItem['treatment_slip']) => val?.name || '-',
        },
        {
            title: t('services.col_image'),
            dataIndex: "image",
            render: (val: string | null) => val ? (
                <img src={val} alt="" className="w-12 h-12 object-cover rounded" />
            ) : '-',
        },
        {
            title: t('services.col_status'),
            dataIndex: "status",
            sorter: true,
            render: (val: number) => {
                const s = STATUS_MAP[val] || STATUS_MAP[0];
                return <Tag color={s.color}>{s.label}</Tag>;
            },
        },
        {
            key: "actions",
            title: t('services.col_action'),
            fixed: "right" as const,
            lockVisible: true,
            render: (_: any, record: ServiceCategoryItem) => (
                <div className="flex items-center gap-3 text-[16px]">
                    {canEdit && (
                        <EditOutlined
                            onClick={() => openEdit(record)}
                            className="text-blue-500 cursor-pointer"
                        />
                    )}

                    {canDelete && (
                        <Popconfirm
                            title={t('services.confirm_delete')}
                            onConfirm={() => handleDelete(record.id)}
                        >
                            <DeleteOutlined className="text-red-500 cursor-pointer" />
                        </Popconfirm>
                    )}
                </div>
            ),
        },
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings(
        "service_category_list",
        (canEdit || canDelete) ? columns : columns.filter((c: any) => c.key !== "actions")
    );

    return (
        <Layout>
            <Helmet>
                <title>{t('page.services')} | Khothi</title>
            </Helmet>
            <div>
                <h2 className="text-xl font-bold text-gray-900 text-000000e0 mb-4">
                    {t('services.title')}
                </h2>
            </div>
            {/* HEADER */}
            <div className="flex justify-between mb-4 gap-4 flex-col md:flex-row items-start md:items-center">

                {/* TABS */}
                <div className="flex gap-2">
                    <button
                        onClick={() => setTab("all")}
                        className={`text-[14px] px-3 py-1 rounded ${tab === "all" ? "bg-gray-800 text-white" : "bg-gray-100"
                            }`}
                    >
                        {t('services.tab_all')}
                    </button>

                    <button
                        onClick={() => setTab("active")}
                        className={`text-[14px] px-3 py-1 rounded ${tab === "active"
                            ? "bg-green-600 text-white"
                            : "bg-gray-100"
                            }`}
                    >
                        {t('services.tab_active')} ({countActive})
                    </button>

                    <button
                        onClick={() => setTab("locked")}
                        className={`text-[14px] px-3 py-1 rounded ${tab === "locked"
                            ? "bg-red-500 text-white"
                            : "bg-gray-100"
                            }`}
                    >
                        {t('services.tab_locked')} ({countLocked})
                    </button>
                </div>

                {/* SEARCH + ADD */}
                <div className="flex gap-2 flex-col md:flex-row w-full md:w-auto">
                    <Input
                        prefix={<SearchOutlined />}
                        placeholder={t('services.search')}
                        style={{ width: 250 }}
                        value={keyword}
                        onChange={(e) => handleSearch(e.target.value)}
                    />

                    {canCreate && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openAdd}
                            className="max-w-[250px]"
                        >
                            {t('services.add')}
                        </Button>
                    )}
                </div>
            </div>

            <div className="flex justify-between items-center mt-4 mb-2">
                <span className="text-sm text-gray-500">
                    {t('services.total')} {pagination.total} {t('services.total_suffix')}
                </span>
                <ColumnSettingButton {...columnSettingProps} />
            </div>
            <Spin spinning={loading}>
                <Table
                    columns={tableColumns}
                    components={tableComponents}
                    tableLayout={tableLayout}
                    dataSource={data}
                    rowKey="id"
                    scroll={{ x: scrollX }}
                    className="custom-ant-table"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        position: ["bottomRight"],
                    }}
                    onChange={handleTableChange}
                />
            </Spin>

            {/* MODAL */}
            <Modal
                title={editing ? t('services.modal_edit') : t('services.modal_add')}
                open={open}
                onCancel={() => setOpen(false)}
                onOk={handleSubmit}
                okText={t('services.save')}
                confirmLoading={submitting}
                className="dark:[&_.ant-modal-content]:bg-[#1f1f1f] 
                dark:[&_.ant-modal-header]:bg-[#1f1f1f]
                dark:[&_.ant-modal-title]:text-white"
            >
                <Form
                    layout="vertical"
                    form={form}
                    className="grid grid-cols-1 gap-3"
                >
                    {/* TÊN */}
                    <Form.Item
                        label={t('services.label_name')}
                        name="name"
                        rules={[{ required: true, message: t('services.name_required') }]}
                    >
                        <Input placeholder={t('services.placeholder_name')} />
                    </Form.Item>

                    {/* MÔ TẢ */}
                    <Form.Item
                        label={t('services.label_description')}
                        name="description"
                    >
                        <Input.TextArea rows={3} placeholder={t('services.placeholder_description')} />
                    </Form.Item>

                    {/* HÌNH ẢNH */}
                    <Form.Item
                        label={t('services.label_image')}
                        name="image"
                    >
                        <Input placeholder={t('services.placeholder_image')} />
                    </Form.Item>

                    {/* STATUS SWITCH */}
                    <Form.Item
                        label="Phiếu chuyên môn"
                        name="treatment_slip_id"
                    >
                        <Select
                            allowClear
                            showSearch
                            loading={loadingTreatmentSlips}
                            placeholder="Chọn phiếu chuyên môn"
                            options={treatmentSlipOptions.map((item) => ({
                                value: item.id,
                                label: item.name,
                            }))}
                            filterOption={(input, option) => {
                                const keyword = normalizeVietnamese(input);
                                const label = normalizeVietnamese(String(option?.label || ""));
                                return label.includes(keyword);
                            }}
                        />
                    </Form.Item>

                    {/* STATUS SWITCH */}
                    <Form.Item
                        label={t('services.label_status')}
                        name="status"
                        valuePropName="checked"
                    >
                        <Switch
                            checkedChildren={t('services.status_active')}
                            unCheckedChildren={t('services.status_locked')}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
}